/**
 * @file src/core/progress.js
 * ctx.progress — tiến trình học. Tiết chỉ cần gọi start() / completeStep() / complete().
 * Engine tự điền attemptId, sessionId, objectives, checkpoint, seq, debounce.
 *
 * Schema payload ĐÃ ĐÓNG BĂNG (map được sang SCORM 2004 và xAPI về sau
 * mà không cần lên protocol 2.0). Xem AGENT.md mục 6.4.
 */

/**
 * @typedef {Object} ProgressApi
 * @property {(n: number) => void} setStepCount
 * @property {() => void} start
 * @property {(stepId: string) => void} completeStep
 * @property {(id: string, o: {state: string, completion: number, scoreScaled?: number|null, labelKey?: string}) => void} setObjective
 * @property {(o: {scaled: number|null, raw?: number, min?: number, max?: number}) => void} setScore
 * @property {() => void} interaction
 * @property {(data: Record<string, unknown>) => void} setCheckpoint
 * @property {() => void} complete
 * @property {(reasonKey?: string) => void} fail
 * @property {() => Record<string, any>} snapshot  Debug panel đọc
 */

/**
 * @param {{ lessonId: string, bridge: import('./bridge.js').Bridge, params: URLSearchParams, isActive: () => boolean }} opts
 * @returns {ProgressApi}
 */
export function createProgress(opts) {
  const attemptId = opts.params.get('attempt') ?? cryptoId();
  const sessionId = cryptoId();

  let stepCount = 1;
  /** @type {Set<string>} */
  const doneSteps = new Set();
  /** @type {Map<string, any>} */
  const objectives = new Map();
  let state = 'started';
  let interactionCount = 0;
  /** @type {{scaled: number|null, raw: number|null, min: number|null, max: number|null}} */
  let score = { scaled: null, raw: null, min: null, max: null };
  /** @type {Record<string, unknown>} */
  let custom = {};
  let stepId = opts.params.get('step') ?? null;

  // timeSpentMs chỉ tính lúc ACTIVE — đó là lý do cần isActive()
  let activeSince = performance.now();
  let timeSpentMs = 0;
  let seq = 0;

  const accumulate = () => {
    const now = performance.now();
    if (opts.isActive()) timeSpentMs += now - activeSince;
    activeSince = now;
  };

  const completion = () => (stepCount <= 0 ? 0 : Math.min(1, doneSteps.size / stepCount));

  const build = () => {
    accumulate();
    return {
      lessonId: opts.lessonId,
      attemptId,
      sessionId,
      state,
      completion: Number(completion().toFixed(4)),
      scoreScaled: score.scaled,
      scoreRaw: score.raw,
      scoreMin: score.min,
      scoreMax: score.max,
      objectives: [...objectives.values()],
      timeSpentMs: Math.round(timeSpentMs),
      interactionCount,
      checkpoint: {
        stepId,
        stepIndex: doneSteps.size,
        stepCount,
        custom,
      },
      ext: {},
      seq: ++seq,
    };
  };

  const send = (immediate = false) => opts.bridge.emitProgress(build(), { immediate });

  return {
    setStepCount(n) {
      stepCount = Math.max(1, Math.floor(n));
    },
    start() {
      state = 'started';
      send(true);
      state = 'progressing';
    },
    completeStep(id) {
      doneSteps.add(id);
      stepId = id;
      send();
    },
    setObjective(id, o) {
      objectives.set(id, {
        id,
        state: o.state,
        completion: o.completion,
        scoreScaled: o.scoreScaled ?? null,
        labelKey: o.labelKey,
      });
      send();
    },
    setScore(o) {
      score = {
        scaled: o.scaled,
        raw: o.raw ?? null,
        min: o.min ?? null,
        max: o.max ?? null,
      };
      send();
    },
    interaction() {
      interactionCount++;
      // KHÔNG send — tránh spam. Sẽ đi kèm lần send tiếp theo.
    },
    setCheckpoint(data) {
      custom = { ...custom, ...data };
    },
    complete() {
      state = 'completed';
      send(true);
    },
    fail(reasonKey) {
      state = 'failed';
      if (reasonKey) custom = { ...custom, failReason: reasonKey };
      send(true);
    },
    snapshot: build,
  };
}

function cryptoId() {
  try {
    return crypto.randomUUID().slice(0, 8);
  } catch {
    return Math.random().toString(36).slice(2, 10);
  }
}
